import { useCallback, useEffect, useMemo, useState } from "react";
import { Autocomplete, Chip, TextField, Typography } from "@mui/material";

function normalizeEmailAddress(value) {
	const raw = String(value || "").trim();
	if (!raw) return "";
	const angleMatch = raw.match(/<([^<>\s]+@[^<>\s]+)>/);
	if (angleMatch?.[1]) return angleMatch[1].trim().toLowerCase();
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw) ? raw.toLowerCase() : "";
}

function isValidEmailAddress(value) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function extractRecipientLookupKey(value) {
	const normalizedEmail = normalizeEmailAddress(value);
	if (normalizedEmail) return normalizedEmail.split("@")[0];
	return String(value || "")
		.trim()
		.toLowerCase()
		.replace(/[<>]/g, "")
		.replace(/,$/, "");
}

function extractRecipientSearchTokens(value) {
	const raw = String(value || "")
		.trim()
		.toLowerCase();
	if (!raw) return [];
	if (raw.includes("@")) {
		const [localPart, domain = ""] = raw.split("@");
		if (domain && !domain.startsWith("ualberta")) return [];
		return localPart ? [localPart] : [];
	}
	return raw
		.replace(/,/g, " ")
		.split(/\s+/)
		.map((token) => token.trim())
		.filter(Boolean);
}

export function buildFacultyRecipientOption(entry) {
	const ccid = String(entry?.ccid || "")
		.trim()
		.toLowerCase();
	if (!ccid) return null;
	const email = `${ccid}@ualberta.ca`;
	const name = String(entry?.name || "").trim();
	const empid = String(entry?.empid || "").trim();
	const rank = String(entry?.rank || "").trim();
	return {
		id: `faculty:${ccid}`,
		kind: "faculty",
		ccid,
		email,
		name,
		empid,
		rank,
		label: name ? `${name} <${email}>` : email,
		searchText: [name, ccid, email, empid, rank].join(" ").toLowerCase(),
	};
}

function buildFreeformRecipientOption(value) {
	const email = normalizeEmailAddress(value);
	if (!email || !isValidEmailAddress(email)) return null;
	return {
		id: `email:${email}`,
		kind: "freeform",
		email,
		label: email,
		searchText: email,
	};
}

export function getRecipientChipLabel(recipient) {
	if (!recipient) return "";
	if (recipient.kind === "faculty" && recipient.name) {
		return `${recipient.name} <${recipient.email}>`;
	}
	return recipient.email || recipient.label || "";
}

function resolveRecipientValue(value, facultyByCcid) {
	if (!value) return null;
	if (typeof value !== "string" && value.email) {
		return value;
	}

	const raw = String(value || "").trim();
	if (!raw) return null;

	const lookupKey = extractRecipientLookupKey(raw);
	const normalizedEmail = normalizeEmailAddress(raw);
	const facultyRecipient = facultyByCcid.get(lookupKey);

	if (facultyRecipient) {
		if (!normalizedEmail) return facultyRecipient;
		if (normalizedEmail === facultyRecipient.email || normalizedEmail.endsWith("@ualberta.ca")) {
			return facultyRecipient;
		}
	}

	if (normalizedEmail && isValidEmailAddress(normalizedEmail)) {
		return buildFreeformRecipientOption(normalizedEmail);
	}

	return null;
}

export function normalizeRecipientSelection(values, facultyByCcid) {
	const next = [];
	const seen = new Set();
	const flattened = [];

	values.forEach((value) => {
		if (typeof value === "string") {
			flattened.push(
				...String(value)
					.split(/[\n,;]+/)
					.map((token) => token.trim())
					.filter(Boolean),
			);
			return;
		}
		flattened.push(value);
	});

	flattened.forEach((value) => {
		const recipient = resolveRecipientValue(value, facultyByCcid);
		const email = recipient?.email?.toLowerCase();
		if (!email || seen.has(email)) return;
		seen.add(email);
		next.push(recipient);
	});

	return next;
}

export function parseEmailAddresses(value) {
	return Array.from(
		new Set(
			String(value || "")
				.split(/[\n,;]+/)
				.map((entry) => entry.trim().toLowerCase())
				.filter(Boolean),
		),
	);
}

export function extractRecipientEmails(recipients) {
	return Array.from(
		new Set(
			(recipients || [])
				.map((recipient) =>
					String(recipient?.email || "")
						.trim()
						.toLowerCase(),
				)
				.filter(Boolean),
		),
	);
}

function normalizeSingleRecipientSelection(value, facultyByCcid) {
	return normalizeRecipientSelection(value ? [value] : [], facultyByCcid)[0] || null;
}

function getSingleRecipientDisplayValue(value) {
	if (!value) return "";
	if (typeof value === "string") return value;
	return value.email || value.label || "";
}

export default function EmailRecipientField(props) {
	const {
		label,
		placeholder,
		value,
		onChange,
		facultyOptions,
		facultyByCcid,
		loading = false,
		error = false,
		helperText = "",
		multiple = true,
		inputAutoComplete,
		inputName,
		inputId,
		shouldShrink = true,
	} = props;
	const [inputValue, setInputValue] = useState("");

	useEffect(() => {
		if (multiple) return;
		setInputValue(getSingleRecipientDisplayValue(value));
	}, [multiple, value]);

	const filteredOptions = useMemo(() => {
		const tokens = extractRecipientSearchTokens(inputValue);
		if (tokens.length === 0) return [];
		return facultyOptions
			.filter((option) => tokens.every((token) => option.searchText.includes(token)))
			.slice(0, 8);
	}, [facultyOptions, inputValue]);

	const commitFreeformInput = useCallback(() => {
		if (!inputValue.trim()) return;
		if (multiple) {
			onChange(
				normalizeRecipientSelection(
					[...(Array.isArray(value) ? value : []), inputValue],
					facultyByCcid,
				),
			);
		} else {
			onChange(normalizeSingleRecipientSelection(inputValue, facultyByCcid));
		}
		setInputValue("");
	}, [facultyByCcid, inputValue, multiple, onChange, value]);

	return (
		<Autocomplete
			id={inputId}
			multiple={multiple}
			freeSolo
			autoHighlight
			size="small"
			value={multiple ? (Array.isArray(value) ? value : []) : value || null}
			inputValue={inputValue}
			options={filteredOptions}
			loading={loading}
			filterOptions={(options) => options}
			filterSelectedOptions={multiple}
			clearOnBlur={false}
			selectOnFocus
			handleHomeEndKeys
			onChange={(_event, newValue) => {
				if (multiple) {
					onChange(normalizeRecipientSelection(newValue, facultyByCcid));
				} else {
					onChange(normalizeSingleRecipientSelection(newValue, facultyByCcid));
				}
				setInputValue("");
			}}
			onInputChange={(_event, newInputValue, reason) => {
				if (reason === "reset") {
					setInputValue("");
					return;
				}
				setInputValue(newInputValue);
			}}
			onKeyDown={(event) => {
				if (!inputValue.trim()) return;
				if (event.key === "," || event.key === ";") {
					event.preventDefault();
					commitFreeformInput();
				}
			}}
			getOptionLabel={(option) => {
				if (typeof option === "string") return option;
				return option?.email || "";
			}}
			isOptionEqualToValue={(option, selected) =>
				String(option?.email || "").toLowerCase() === String(selected?.email || "").toLowerCase()
			}
			noOptionsText={
				inputValue.trim() ? "Press Enter to use this address" : "Start typing a CCID or email"
			}
			renderTags={
				multiple
					? (tagValue, getTagProps) =>
							tagValue.map((recipient, index) => {
								const { key, ...tagProps } = getTagProps({ index });
								return (
									<Chip
										key={key}
										{...tagProps}
										label={getRecipientChipLabel(recipient)}
										size="small"
									/>
								);
							})
					: undefined
			}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;
				return (
					<li key={key} {...optionProps}>
						<div className="flex min-w-0 flex-col">
							<Typography fontWeight={600}>{option.name || option.email}</Typography>
							<Typography variant="caption" color="text.secondary">
								{option.email}
								{option.empid ? ` • ${option.empid}` : ""}
								{option.rank ? ` • ${option.rank}` : ""}
							</Typography>
						</div>
					</li>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					id={inputId}
					className="bg-white"
					label={label}
					placeholder={multiple ? (value?.length === 0 ? placeholder : "") : placeholder}
					error={error}
					helperText={helperText}
					slotProps={{
						inputLabel: { shrink: shouldShrink },
						htmlInput: {
							...(params.inputProps || {}),
							autoComplete: inputAutoComplete || (label === "To" ? "email" : "new-password"),
							name: inputName,
						},
					}}
				/>
			)}
		/>
	);
}
